<template>
  <div class="sp-bottom-sheet" :class="classModifiers">
    <slot name="activator" @click.stop="handleActivatorClick" />
    <sp-dialog
      position="bottom"
      width="100vw"
      :label="label"
      :persistent="persistent"
      :open="open"
      :no-restore-focus="noRestoreFocus"
      :is-hidden-temporarily="isHiddenTemporarily"
      no-scrollable
      :lock-scroll="lockScroll"
      @close="handleDialogClose"
      @update-open="handleDialogUpdateOpen"
    >
      <div class="sp-bottom-sheet__content">
        <div class="sp-bottom-sheet__dialog-slot">
          <slot />
        </div>
        <div class="sp-bottom-sheet__action">
          <slot name="action" @click="handleActionClick" />
        </div>
      </div>
    </sp-dialog>
  </div>
</template>

<script setup>
/**
 * The `sp-bottom-sheet` component wraps `sp-dialog` anchored to the bottom of the
 * screen as a sheet. It delegates the open/close lifecycle (state machine,
 * transitions, focus, scroll lock) to the inner dialog and owns its own sheet
 * concerns: the visible-viewport sizing and the expand/padding modifiers.
 *
 * @slot default - The sheet body.
 * @slot action - Action content pinned below the scrollable body.
 * @slot activator - The element that opens the sheet when clicked.
 *
 * @emits close - Emitted when the sheet closes.
 * @emits update-open - Emitted when the open state changes.
 */
import { computed } from "vue";
import { useDialogBridge } from "#ce/composables/useDialogBridge.js";
import { toBoolean } from "#ce/utils/props";
import { props as sheetProps } from "./props.js";
import { useViewportHeight } from "./composables/useViewportHeight.js";

const emit = defineEmits(["close", "update-open"]);

const props = defineProps(sheetProps);

const { open, handleDialogUpdateOpen } = useDialogBridge(props, emit);

// Pass `open` so the composable recomputes the viewport height every time the
// sheet opens — see useViewportHeight for why a mount-time value goes stale.
const { viewportUnit, keyboardInset } = useViewportHeight(open);

// `none`, not a zero length, while no keyboard is up: any non-none translate
// makes the dialog a containing block for fixed-position descendants — see the
// `translate` rule in SpDialog.
const keyboardTranslate = computed(() => (keyboardInset.value === "0px" ? "none" : `0 -${keyboardInset.value}`));

const classModifiers = computed(() => ({
  "--expand-height-max": toBoolean(props.expandHeightMax),
  "--expand-to-full-height": toBoolean(props.expandToFullHeight),
  "--no-content-padding": toBoolean(props.noContentPadding),
}));

function handleActivatorClick() {
  open.value = true;
}

function handleActionClick() {
  open.value = false;
}

function handleDialogClose() {
  emit("close");
  open.value = false;
}
</script>

<style>
:host {
  display: block;
  --bottom-sheet-border-radius: var(--sp-comp-bottom-sheet-border-radius, 8px 8px 0 0);
}
</style>

<style lang="scss" scoped>
.sp-bottom-sheet {
  --vh-multiplier: 94;
  --vh-calculated: calc(v-bind(viewportUnit) * var(--vh-multiplier));
  --bottom-sheet-max-height: var(--sp-comp-bottom-sheet-content-max-height, var(--vh-calculated));
  // All three dialog vars are set unconditionally so the sheet owns its
  // dialog's size — an outer surface's --dialog-* values (e.g. SpCheckout's)
  // never leak into a sheet nested inside it.
  --dialog-height: auto;
  --dialog-min-height: auto;
  --dialog-max-height: var(--bottom-sheet-max-height);
  // Clamped to the max height, which tracks the visible viewport: with the
  // keyboard up an unclamped minimum would push the sheet's top offscreen.
  --bottom-sheet-min-height: min(var(--sp-comp-bottom-sheet-content-min-height, 0px), var(--bottom-sheet-max-height));
  --dialog-keyboard-translate: v-bind(keyboardTranslate);
  --bottom-sheet-height: auto;

  &.--no-content-padding {
    --sp-comp-bottom-sheet-content-padding-inline: 0 !important;
    --sp-comp-bottom-sheet-content-padding-block: 0 !important;
  }
}

.sp-bottom-sheet.--expand-height-max {
  --bottom-sheet-height: var(--bottom-sheet-max-height);
  --dialog-height: var(--bottom-sheet-max-height);
  --dialog-min-height: var(--bottom-sheet-max-height);
}

.sp-bottom-sheet.--expand-to-full-height {
  --vh-multiplier: 100;
  --bottom-sheet-height: var(--bottom-sheet-max-height);
  --dialog-min-height: var(--bottom-sheet-max-height);
  --dialog-height: var(--bottom-sheet-max-height);
}

.sp-bottom-sheet__content {
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  box-sizing: border-box;
  position: relative;
  max-height: var(--bottom-sheet-max-height);
  min-height: var(--bottom-sheet-min-height);
  height: var(--bottom-sheet-height);
  border-radius: var(--bottom-sheet-border-radius);
  background-color: var(--sp-comp-bottom-sheet-background-color, var(--sp-sys-color-surface, white));
  padding-inline: var(--sp-comp-bottom-sheet-content-padding-inline, 1rem);
  padding-block: var(--sp-comp-bottom-sheet-content-padding-block, 1rem);
}

sp-dialog {
  --sp-comp-dialog-margin: 0;
  --sp-comp-dialog-padding: 0;
  --sp-comp-dialog-background: transparent;
  --sp-comp-dialog-width: 100vw;
  --sp-comp-dialog-border-radius: var(--bottom-sheet-border-radius);
}

.sp-bottom-sheet__dialog-slot {
  position: relative;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.sp-bottom-sheet__action {
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding-inline: var(--sp-comp-bottom-sheet-action-container-padding-inline, 1rem);
  ::slotted(*) {
    margin-top: 1rem;
  }
}
</style>
